"use client"

import { useState, useEffect, useCallback, useMemo } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faTicketAlt,
  faUsers,
  faUserClock,
  faFlag,
  faChevronUp,
  faChevronDown,
  faBell,
  faDownload,
  faSync,
  faHashtag,
  faUser,
  faPhone,
  faClipboardQuestion,
  faUserCheck,
  faInfoCircle,
  faEye,
  faSearch,
  faFilter,
  faChevronLeft,
  faChevronRight,
  faSpinner,
  faLock,
  faUserPlus,
  faClock,
  faExclamationTriangle,
  faEnvelope,
  faHistory,
  faServer,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons"
import { FaListAlt, FaInbox, FaSpinner, FaCheckCircle, FaHourglassHalf, FaFileInvoice, FaTimesCircle } from "react-icons/fa"
import Skeleton from "react-loading-skeleton"
import "react-loading-skeleton/dist/skeleton.css"
import {
  getTicketsByClientId,
  getActiveStaff,
  getStaff,
  getStaffList,
  assignAgentToTickets,
  getTicketCountsByClientId,
  getTicketHistory,
  searchTicketActivity,
  getAMCReport,
  getExpiredAMCReport,
  getInactiveAMCReport
} from "../../../services/AdminService"
import adminService from "../../../services/AdminService"

const TicketAdmin = () => {
  const [tickets, setTickets] = useState([])
  const [staffList, setStaffList] = useState([])
  const [loading, setLoading] = useState(true)
  const [staffLoaded, setStaffLoaded] = useState(false)
  const [filterStatus, setFilterStatus] = useState("All")
  const [fromDate, setFromDate] = useState("")
  const [toDate, setToDate] = useState("")
  const [dateFilter, setDateFilter] = useState("All")
  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedTicket, setSelectedTicket] = useState(null)
  const [showTicketModal, setShowTicketModal] = useState(false)
  const [assigningTickets, setAssigningTickets] = useState(new Set())
  const [refreshing, setRefreshing] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [showSearchFilters, setShowSearchFilters] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [quickFilter, setQuickFilter] = useState("")
  const [ticketHistory, setTicketHistory] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [rmsFilter, setRmsFilter] = useState("All") // All, RMS, Non-RMS
  const [agentFilter, setAgentFilter] = useState("All") // All, or specific agent ID
  const [deviceFilter, setDeviceFilter] = useState("All") // All, or specific device/sticker ID
  const [showAllDevices, setShowAllDevices] = useState(false)
  // Billing Modal States
  const [showBillingModal, setShowBillingModal] = useState(false)
  const [billingTicketId, setBillingTicketId] = useState(null)
  const [billingStatus, setBillingStatus] = useState("")
  const [billingComment, setBillingComment] = useState("")
  const [billingLoading, setBillingLoading] = useState(false)

  const pageSize = 15
  const [clientId, setClientId] = useState(null)
  const [deviceSubscriptionMap, setDeviceSubscriptionMap] = useState({})
  const [deviceSubscriptionDetailsMap, setDeviceSubscriptionDetailsMap] = useState({})
  const abbreviation = (localStorage.getItem("abbreviation") || "").toUpperCase()

  // Toast States
  const [toasts, setToasts] = useState([])

  const [ticketCounts, setTicketCounts] = useState({
    totalCount: 0,
    openCount: 0,
    inProgressCount: 0,
    resolvedCount: 0,
    invoicedCount: 0,
    notInvoicedCount: 0,
    noResponseCount: 0,
  })
  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedClientId = sessionStorage.getItem("clientId")
      if (storedClientId) {
        setClientId(storedClientId)
      }
    }
  }, [])
  const processComplaintDisplay = (ticket) => {
    const complaints = ticket.complaints || []

    // Check if this is an RMS ticket
    const isRMS = complaints.some((complaint) => {
      const complaintText =
        typeof complaint === "object"
          ? complaint.complaint_detail || complaint.complaint_description || ""
          : complaint || ""
      return complaintText.toLowerCase().includes("rms")
    })

    if (isRMS) {
      // For RMS tickets, show only "RMS Schedule Request" once
      return {
        type: "RMS",
        displayText: "RMS Schedule Request",
        count: 1,
      }
    } else {
      // For regular complaints, show all unique complaints
      const uniqueComplaints = [
        ...new Set(
          complaints.map((complaint) =>
            typeof complaint === "object"
              ? complaint.complaint_detail || complaint.complaint_description || "No description"
              : complaint || "No description",
          ),
        ),
      ]

      return {
        type: "COMPLAINT",
        displayText: uniqueComplaints,
        count: uniqueComplaints.length,
      }
    }
  }

  const getTicketDeviceKey = (ticket) => {

    if (!Array.isArray(ticket.complaints)) return null

    const deviceId = ticket.complaints[0]?.device_id

    if (!deviceId) return null

    // use mobile because your subscription table is linked with mobile
    const mobile = ticket.complaintmobile

    if (!mobile) return null

    return mobile + "_" + deviceId
  }

  const normalizeValue = (value) => (typeof value === "string" ? value.trim().toLowerCase() : "")

  const parseDateValue = (value) => {
    if (!value) return null
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date
  }

  const getLatestSubscriptionRecord = (currentRecord, nextRecord) => {
    if (!currentRecord) return nextRecord

    const currentEnd = parseDateValue(currentRecord.planEnd)?.getTime() ?? -Infinity
    const nextEnd = parseDateValue(nextRecord.planEnd)?.getTime() ?? -Infinity
    if (nextEnd !== currentEnd) {
      return nextEnd > currentEnd ? nextRecord : currentRecord
    }

    const currentStart = parseDateValue(currentRecord.planStart)?.getTime() ?? -Infinity
    const nextStart = parseDateValue(nextRecord.planStart)?.getTime() ?? -Infinity
    if (nextStart !== currentStart) {
      return nextStart > currentStart ? nextRecord : currentRecord
    }

    const currentCreated = parseDateValue(currentRecord.createdAt)?.getTime() ?? -Infinity
    const nextCreated = parseDateValue(nextRecord.createdAt)?.getTime() ?? -Infinity
    if (nextCreated !== currentCreated) {
      return nextCreated > currentCreated ? nextRecord : currentRecord
    }

    return nextRecord
  }

  const getTicketSubscriptionDetails = (ticket) => {
    const key = getTicketDeviceKey(ticket)
    return key ? deviceSubscriptionDetailsMap[key] || null : null
  }

  const isTicketCoveredByLatestAMC = (ticket) => {
    const subscription = getTicketSubscriptionDetails(ticket)
    if (!subscription || subscription.status === "INACTIVE") return false

    const ticketCreatedAt = parseDateValue(ticket.created_at)
    const planStart = parseDateValue(subscription.planStart)
    const planEnd = parseDateValue(subscription.planEnd)

    if (!ticketCreatedAt || !planStart || !planEnd) return false

    return ticketCreatedAt >= planStart && ticketCreatedAt <= planEnd
  }

  const isTruthyFlag = (value) => {
    if (value === true || value === 1) return true
    const normalized = normalizeValue(value)
    return normalized === "true" || normalized === "yes" || normalized === "1" || normalized === "active"
  }

  const isNotInAMC = (ticket) => {
    if (isTicketCoveredByLatestAMC(ticket)) return false

    const subscriptionStatus = deviceSubscriptionMap[getTicketDeviceKey(ticket)] || ""
    const amcStatus = normalizeValue(ticket.amcStatus)
    const contractStatus = normalizeValue(ticket.contractStatus)
    const coverageType = normalizeValue(ticket.coverageType)
    const serviceType = normalizeValue(ticket.serviceType)

    return (
      isTruthyFlag(ticket.isChargeable) ||
      isTruthyFlag(ticket.amcExpired) ||
      amcStatus === "expired" ||
      contractStatus === "expired" ||
      coverageType === "chargeable" ||
      serviceType === "chargeable" ||
      coverageType === "non amc" ||
      coverageType === "not in amc" ||
      coverageType === "not covered" ||
      serviceType === "non amc" ||
      serviceType === "not in amc" ||
      serviceType === "call basis service" ||
      subscriptionStatus === "EXPIRED" ||
      subscriptionStatus === "INACTIVE" ||
      subscriptionStatus === ""
    )
  }

  const getServiceType = (ticket) => {
    if (isTicketCoveredByLatestAMC(ticket)) {
      return {
        label: "AMC/WRT Covered",
        class: "badge bg-success",
        icon: faCheckCircle
      }
    }

    const key = getTicketDeviceKey(ticket)

    if (!key) {
      return {
        label: "Call Basis Service",
        class: "badge bg-warning text-dark",
        icon: faExclamationTriangle
      }
    }

    const status = deviceSubscriptionMap[key]

    if (status === "EXPIRED") {
      return {
        label: "Not In AMC - Chargeable",
        class: "badge bg-warning text-dark",
        icon: faExclamationTriangle
      }
    }

    if (status === "INACTIVE") {
      return {
        label: "Discontinued - Chargeable",
        class: "badge bg-danger",
        icon: faLock
      }
    }

    if (status === "AMC") {
      return {
        label: "AMC/WRT Covered",
        class: "badge bg-success",
        icon: faCheckCircle
      }
    }

    return {
      label: "Call Basis Service",
      class: "badge bg-warning text-dark",
      icon: faExclamationTriangle
    }
  }

  // Check if ticket can be assigned (only OPN and INP status)
  const canAssignTicket = (status) => {
    const code = status?.toUpperCase()
    return code === "OPN" || code === "INP"
  }

  const canUpdateFinalBillingStatus = (status) => {
    const code = status?.toUpperCase();
    return abbreviation === "T" && ["CRS", "INV", "NIV"].includes(code);
  };

  // Check if ticket is from new visitor
  const isNewVisitor = (remarks) => {
    if (!remarks) return false
    const lowerRemarks = remarks.toLowerCase()
    return (
      lowerRemarks.includes("new visitor") ||
      lowerRemarks.includes("not registered") ||
      lowerRemarks.includes("first time") ||
      lowerRemarks.includes("initial level")
    )
  }

  // Detect Service Type (AMC / Expired / Discontinued)

  // Quick filter options
  const quickFilters = [
    { key: "unassigned", label: "Unassigned", icon: faUserPlus },
    { key: "not_in_amc", label: "Not In AMC - Chargeable", icon: faExclamationTriangle },
    // { key: "new_visitors", label: "New Visitors", icon: faFlag },
    { key: "today", label: "Today", icon: faClock },
  ]

  // Toast function
  const showToast = (message, type = "error", duration = 4000) => {
    const id = Date.now() + Math.random()
    const newToast = { id, message, type, duration }
    setToasts((prev) => [...prev, newToast])

    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id))
    }, duration)
  }

  // Auto refresh functionality
  const refreshData = useCallback(async () => {
    setRefreshing(true)

    const staffData = staffList.length > 0
      ? staffList
      : await fetchStaffData()

    await fetchAllData(staffData)
    await fetchTicketCounts()

    setRefreshing(false)
  }, [staffList])

  useEffect(() => {
    let interval
    if (autoRefresh) {
      interval = setInterval(() => {
        refreshData()
      }, 30000)
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [autoRefresh, refreshData])

  // Toast Component
  const ToastContainer = () => (
    <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1055 }}>
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`alert alert-${toast.type === "error" ? "danger" : "success"} alert-dismissible fade show`}
          role="alert"
        >
          <FontAwesomeIcon icon={toast.type === "error" ? faExclamationTriangle : faInfoCircle} className="me-2" />
          <strong>{toast.type === "error" ? "Error!" : "Success!"}</strong> {toast.message}
          <button
            type="button"
            className="btn-close"
            onClick={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
          ></button>
        </div>
      ))}
    </div>
  )

  const statusConfig = {
    All: { icon: FaListAlt, color: "primary", bgClass: "bg-primary", description: "Total Tickets" },
    Open: { icon: FaInbox, color: "success", bgClass: "bg-success", description: "New & Open" },
    "In-Progress": { icon: FaSpinner, color: "warning", bgClass: "bg-warning", description: "Being Worked" },
    Resolved: { icon: FaCheckCircle, color: "info", bgClass: "bg-info", description: "Invoice Pending" },
    Invoiced: { icon: FaFileInvoice, color: "dark", bgClass: "bg-dark", description: "Invoice Generated" },
    "Not-Invoiced": { icon: FaTimesCircle, color: "secondary", bgClass: "bg-secondary", description: "FOC" },
    "No-Response": { icon: FaHourglassHalf, color: "danger", bgClass: "bg-danger", description: "Not Interested" },
  }

  const statusKeywords = {
    Open: ["OPN"],
    "In-Progress": ["INP"],
    Resolved: ["CRS"],
    "No-Response": ["CNR"],
    Invoiced: ["INV"],
    "Not-Invoiced": ["NIV"],
  }

  const getStatusDescription = (code) => {
    switch (code?.toUpperCase()) {
      case "OPN":
        return "Open"
      case "INP":
        return "In Progress"
      case "CRS":
        return "Closed"
      case "CNR":
        return "No Response"
      case "INV": return "Invoiced"
      case "NIV": return "Not Invoiced"
      default:
        return code || "Unknown"
    }
  }

  const getBadgeClass = (status) => {
    const code = status?.toUpperCase()
    if (code === "OPN") return "badge rounded-pill bg-success text-white fw-semibold"
    if (code === "INP") return "badge rounded-pill bg-warning text-dark fw-semibold"
    if (code === "CRS") return "badge rounded-pill bg-info text-white fw-semibold"
    if (code === "CNR") return "badge rounded-pill bg-danger text-white fw-semibold"
    if (code === "INV") return "badge rounded-pill bg-dark text-white fw-semibold";
    if (code === "NIV") return "badge rounded-pill bg-secondary text-white fw-semibold";
    return "badge rounded-pill bg-secondary text-white fw-semibold"
  }

  const fetchAllData = async (staffData) => {
    setLoading(true)
    try {
      const ticketRes = await getTicketsByClientId(clientId)
      const ticketArr = Array.isArray(ticketRes?.tickets) ? ticketRes.tickets : []

      const merged = ticketArr.map((t) => {
        const assignedToId = Number.parseInt(t.assignedTo || t.assigned_to)
        const assignedStaff = staffData.find((s) => Number.parseInt(s.user_id) === assignedToId)

        return {
          ...t,
          assigned_to: assignedToId,
          assignedName: assignedStaff?.user_name || "Unassigned",
          activity_description: t.activity_description || t.remarks || "No Remark",
          created_at: t.created_at || t.createdAt || null,
          complaintProcessed: processComplaintDisplay(t),
        }
      })

      setTickets(merged)
    } catch (err) {
      console.error("Error fetching tickets:", err)
      setTickets([])
      showToast("Failed to load tickets. Please refresh the page.")
    } finally {
      setLoading(false)
    }
  }

  const fetchStaffData = async () => {
    try {
      let staffData = []

      console.log("Using API:", abbreviation === "T" ? "active-staff" : "manage-user")

      if (abbreviation === "T") {
        const result = await getActiveStaff(clientId)
        if (result?.data?.data) {
          staffData = result.data.data
        }
      } else {
        // Try primary method first
        const result = await getStaff()

        if (result?.data && Object.keys(result.data).length > 0) {
          staffData = Object.values(result.data)
        } else {
          // Fallback to alternative staff API
          try {
            const alternativeResult = await getStaffList(clientId)
            staffData = Array.isArray(alternativeResult) ? alternativeResult : []
          } catch (fallbackError) {
            console.error("Fallback staff fetch failed:", fallbackError)
          }
        }
      }

      staffData = Array.isArray(staffData)
        ? staffData.filter((user) => Number(user?.status) === 1)
        : []

      setStaffList(staffData)
      setStaffLoaded(true)
      return staffData
    } catch (error) {
      console.error("Error fetching staff:", error)
      showToast("Failed to load staff list.")
      return []
    }
  }
  const loadSubscriptionStatus = async () => {

    try {

      const [amcRes, expiredRes, inactiveRes] = await Promise.all([
        getAMCReport(),
        getExpiredAMCReport(),
        getInactiveAMCReport()
      ])

      const map = {}
      const detailsMap = {}

      const addSubscriptionRecords = (items, status) => {
        if (!Array.isArray(items)) return

        items.forEach(item => {
          const key = item.ContactNumber + "_" + item.deviceID
          const nextRecord = {
            status,
            planStart: item.planStart,
            planEnd: item.planEnd,
            createdAt: item.createdAt,
          }

          const latestRecord = getLatestSubscriptionRecord(detailsMap[key], nextRecord)
          detailsMap[key] = latestRecord
          map[key] = latestRecord.status
        })
      }

      addSubscriptionRecords(amcRes?.data, "AMC")
      addSubscriptionRecords(expiredRes?.data, "EXPIRED")
      addSubscriptionRecords(inactiveRes?.data, "INACTIVE")

      setDeviceSubscriptionMap(map)
      setDeviceSubscriptionDetailsMap(detailsMap)

    } catch (error) {

      console.error("Failed to load subscription data", error)

    }

  }
  useEffect(() => {
    if (!clientId) return
    const initialize = async () => {
      try {

        setLoading(true)
        await loadSubscriptionStatus()
        // 1️⃣ Fetch staff first
        const staffData = await fetchStaffData()

        // 2️⃣ Always fetch tickets (even if staff empty)
        await fetchAllData(staffData || [])

        // 3️⃣ Fetch counts
        await fetchTicketCounts()

      } catch (error) {
        console.error("Initialization error:", error)
        showToast("Failed to initialize ticket management.")
      } finally {
        setLoading(false)
      }
    }

    initialize()
  }, [clientId])
  const handleAssignAgent = async (ticketId, staffId) => {
    const createdBy = Number.parseInt(sessionStorage.getItem("userId")) || 0

    setAssigningTickets((prev) => new Set([...prev, ticketId]))

    try {
      const result = await assignAgentToTickets(ticketId, staffId, createdBy)

      if (result?.status) {
        showToast("Ticket assigned successfully!", "success")
        await fetchAllData(staffList)
      } else {
        showToast(result?.message || "Failed to assign agent. Please try again.")
      }
    } catch (err) {
      console.error("Assignment failed:", err)
      showToast("Assignment failed due to network error.")
    } finally {
      setAssigningTickets((prev) => {
        const newSet = new Set(prev)
        newSet.delete(ticketId)
        return newSet
      })
    }
  }

  const fetchTicketHistory = async (ticketId) => {
    setHistoryLoading(true)
    try {
      const response = await getTicketHistory(ticketId, clientId)
      if (response?.status === "success" || response?.status === true) {
        setTicketHistory(response.data || [])
      } else {
        // Fallback to search ticket activity
        try {
          const activityResponse = await searchTicketActivity({ ticketId, clientId })
          if (activityResponse?.status) {
            setTicketHistory(activityResponse.data || [])
          } else {
            setTicketHistory([])
          }
        } catch (fallbackError) {
          console.error("Fallback history fetch failed:", fallbackError)
          setTicketHistory([])
        }
      }
    } catch (error) {
      console.error("Error fetching ticket history:", error)
      setTicketHistory([])
    } finally {
      setHistoryLoading(false)
    }
  }

  useEffect(() => {
    if (showTicketModal && selectedTicket?.ticket_id) {
      fetchTicketHistory(selectedTicket.ticket_id)
    }
  }, [showTicketModal, selectedTicket])

  const isWithinDateFilter = (createdDate) => {
    if (!createdDate) return false
    const date = new Date(createdDate)
    const today = new Date()

    if (fromDate && toDate) {
      const from = new Date(fromDate)
      const to = new Date(toDate)
      to.setHours(23, 59, 59, 999)
      return date >= from && date <= to
    }

    switch (dateFilter) {
      case "Today":
        return date.toDateString() === today.toDateString()
      case "ThisWeek":
        const start = new Date(today.setDate(today.getDate() - today.getDay()))
        const end = new Date(start)
        end.setDate(start.getDate() + 6)
        return date >= start && date <= end
      case "ThisMonth":
        return date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear()
      case "ThisYear":
        return date.getFullYear() === today.getFullYear()
      default:
        return true
    }
  }

  const handleFilter = () => {
    if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
      showToast("From date cannot be later than to date.")
      return
    }
    setDateFilter("manual")
  }

  const handleReset = () => {
    setFromDate("")
    setToDate("")
    setDateFilter("All")
    setFilterStatus("All")
    setSearchTerm("")
    setQuickFilter("")
    setRmsFilter("All")
    setAgentFilter("All")
    setDeviceFilter("All")
    setShowAllDevices(false)
    setCurrentPage(1)
  }

  // Device helpers
  const isValidDeviceId = (rawDeviceId) => {
    if (rawDeviceId === undefined || rawDeviceId === null) return false
    const id = String(rawDeviceId).trim()
    if (!id) return false
    return id !== "0"
  }

  const getAllDeviceIds = useCallback((ticketList) => {
    const ids = new Set()
    ticketList.forEach((ticket) => {
      const complaints = Array.isArray(ticket.complaints) ? ticket.complaints : []
      complaints.forEach((complaint) => {
        const rawDeviceId = complaint?.device_id
        if (!isValidDeviceId(rawDeviceId)) return
        ids.add(String(rawDeviceId).trim())
      })
    })
    return ids
  }, [])

  const buildDeviceMap = useCallback((ticketList) => {
    const aggregation = {}

    ticketList.forEach((ticket) => {
      const status = ticket.status?.toUpperCase()
      const ticketId = String(ticket.ticket_id ?? ticket.id ?? "")
      const complaints = Array.isArray(ticket.complaints) ? ticket.complaints : []
      const ticketDeviceIds = new Set()

      complaints.forEach((complaint) => {
        const rawDeviceId = complaint?.device_id
        if (!isValidDeviceId(rawDeviceId)) return

        const deviceId = String(rawDeviceId).trim()
        ticketDeviceIds.add(deviceId)

        if (!aggregation[deviceId]) {
          aggregation[deviceId] = {
            total: 0,
            OPN: 0,
            INP: 0,
            CRS: 0,
            _statusTicketIds: {
              OPN: new Set(),
              INP: new Set(),
              CRS: new Set(),
            },
          }
        }

        aggregation[deviceId].total += 1
      })

      ticketDeviceIds.forEach((deviceId) => {
        const deviceEntry = aggregation[deviceId]
        if (!deviceEntry) return

        if (ticketId && (status === "OPN" || status === "INP" || status === "CRS")) {
          if (!deviceEntry._statusTicketIds[status].has(ticketId)) {
            deviceEntry._statusTicketIds[status].add(ticketId)
            deviceEntry[status] += 1
          }
        }
      })
    })

    return Object.fromEntries(
      Object.entries(aggregation).map(([deviceId, value]) => [
        deviceId,
        {
          total: value.total,
          OPN: value.OPN,
          INP: value.INP,
          CRS: value.CRS,
        },
      ]),
    )
  }, [])

  // Device aggregation for filter dropdown and status insight
  const deviceAggregation = useMemo(() => buildDeviceMap(tickets), [tickets, buildDeviceMap])

  const deviceFilterOptions = useMemo(() => {
    return Array.from(getAllDeviceIds(tickets)).sort((a, b) => {
      const aNum = Number(a)
      const bNum = Number(b)
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum
      return a.localeCompare(b)
    })
  }, [tickets, getAllDeviceIds])

  const DEVICE_COLLAPSE_LIMIT = 10

  const visibleDeviceOptions = useMemo(() => {
    if (showAllDevices) return deviceFilterOptions

    const firstBatch = deviceFilterOptions.slice(0, DEVICE_COLLAPSE_LIMIT)

    if (
      deviceFilter !== "All" &&
      deviceFilterOptions.includes(deviceFilter) &&
      !firstBatch.includes(deviceFilter)
    ) {
      return [...firstBatch, deviceFilter]
    }

    return firstBatch
  }, [showAllDevices, deviceFilterOptions, deviceFilter])

  const hiddenDeviceCount = Math.max(deviceFilterOptions.length - DEVICE_COLLAPSE_LIMIT, 0)

  const filteredTickets = tickets
    .filter((ticket) => {
      if (filterStatus === "All") return true
      return statusKeywords[filterStatus]?.includes(ticket.status)
    })
    .filter((ticket) => dateFilter === "All" || isWithinDateFilter(ticket.created_at))
    .filter((ticket) => {
      if (rmsFilter === "All") return true
      const isRMS = ticket.complaintProcessed?.type === "RMS"
      return rmsFilter === "RMS" ? isRMS : !isRMS
    })
    .filter((ticket) => {
      if (agentFilter === "All") return true
      return ticket.assigned_to?.toString() === agentFilter.toString()
    })
    .filter((ticket) => {
      if (deviceFilter === "All") return true
      const complaints = Array.isArray(ticket.complaints) ? ticket.complaints : []
      return complaints.some((complaint) => String(complaint?.device_id) === String(deviceFilter))
    })
    .filter((ticket) => {
      // Quick filters
      if (quickFilter === "unassigned" && ticket.assignedName !== "Unassigned") return false
      if (quickFilter === "not_in_amc" && !isNotInAMC(ticket)) return false
      if (quickFilter === "new_visitors" && !isNewVisitor(ticket.activity_description)) return false
      if (quickFilter === "today" && !isWithinDateFilter(ticket.created_at)) return false
      return true
    })
    .filter((ticket) => {
      if (!searchTerm) return true
      const searchLower = searchTerm.toLowerCase()
      return (
        ticket.complaintname?.toLowerCase().includes(searchLower) ||
        ticket.complaintmobile?.toLowerCase().includes(searchLower) ||
        ticket.ticket_id?.toString().includes(searchLower) ||
        ticket.assignedName?.toLowerCase().includes(searchLower) ||
        ticket.activity_description?.toLowerCase().includes(searchLower)
      )
    })

  const totalPages = Math.ceil(filteredTickets.length / pageSize)
  const paginatedTickets = filteredTickets.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  const deviceSummaryText =
    deviceFilter === "All"
      ? "Showing all tickets"
      : `Showing ${filteredTickets.length} tickets for Sticker ID: ${deviceFilter}`

  const fetchTicketCounts = async () => {
    try {
      const res = await getTicketCountsByClientId(clientId)
      const data = res?.data || {}
      setTicketCounts({
        totalCount: data.totalCount || 0,
        openCount: data.openCount || 0,
        inProgressCount: data.inProgressCount || 0,
        resolvedCount: data.resolvedCount || 0,
        invoicedCount: data.invoicedCount || 0,
        notInvoicedCount: data.notInvoicedCount || 0,
        noResponseCount: data.noResponseCount || 0,
      })
    } catch (err) {
      console.error("Count API failed", err)
    }
  }

  const statusCounts = {
    All: ticketCounts.totalCount,
    Open: ticketCounts.openCount,
    "In-Progress": ticketCounts.inProgressCount,
    Resolved: ticketCounts.resolvedCount,
    Invoiced: ticketCounts.invoicedCount,
    "Not-Invoiced": ticketCounts.notInvoicedCount,
    "No-Response": ticketCounts.noResponseCount,
  }

  // Calculate additional metrics
  const getTicketMetrics = () => {
    const unassignedCount = tickets.filter((t) => t.assignedName === "Unassigned").length
    const newVisitorCount = tickets.filter((t) => isNewVisitor(t.activity_description)).length
    const chargeableCount = tickets.filter((t) => isNotInAMC(t)).length
    const todayTickets = tickets.filter((t) => {
      if (!t.created_at) return false
      const today = new Date().toDateString()
      return new Date(t.created_at).toDateString() === today
    }).length

    return { unassignedCount, newVisitorCount, chargeableCount, todayTickets }
  }

  const { unassignedCount, newVisitorCount, chargeableCount, todayTickets } = getTicketMetrics()

  // Export functionality
  const exportToCSV = () => {
    const headers = ["Ticket ID", "Customer", "Phone", "Status", "Assigned To", "Complaint Type", "Created"]
    const csvData = filteredTickets.map((ticket) => [
      `CBR-${ticket.ticket_id}`,
      ticket.complaintname,
      ticket.complaintmobile || "",
      getStatusDescription(ticket.status),
      ticket.assignedName,
      ticket.complaintProcessed?.type || "Unknown",
      ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : "",
    ])

    const csvContent = [headers, ...csvData].map((row) => row.join(",")).join("\n")
    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `tickets_${new Date().toISOString().split("T")[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }
  // Open Billing Modal
  const openBillingModal = (ticketId, status) => {
    setBillingTicketId(ticketId)
    setBillingStatus(status)
    setBillingComment("")
    setShowBillingModal(true)
  }

  // Submit Billing Status
  const handleBillingSubmit = async () => {
    if (!billingComment || !billingComment.trim()) {
      showToast("Billing comment is required.", "error")
      return
    }

    try {
      setBillingLoading(true)

      const userId = sessionStorage.getItem("userId")

      const payload = {
        ticketId: billingTicketId,
        status: billingStatus,
        description: billingComment,
        createdBy: userId,
      }

      const response = await adminService.updateTicketStatus(payload)

      if (response?.status) {
        showToast("Billing status updated successfully.", "success")
        setShowBillingModal(false)
        setBillingComment("")
        await refreshData()
      } else {
        showToast(response?.message || "Failed to update billing status")
      }
    } catch (error) {
      console.error("Billing update failed:", error)
      showToast("Billing update failed")
    } finally {
      setBillingLoading(false)
    }
  }


  const TicketDetailModal = () => (
    <div
      className={`modal fade ${showTicketModal ? "show" : ""}`}
      style={{ display: showTicketModal ? "block" : "none" }}
      tabIndex="-1"
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content border-0 shadow-lg">
          <div className="modal-header bg-primary text-white border-0">
            <h5 className="modal-title d-flex align-items-center mb-0">
              <FontAwesomeIcon icon={faEye} className="me-2" />
              Ticket Details - CBR-{selectedTicket?.ticket_id}
              {selectedTicket?.complaintProcessed?.type === "RMS" && (
                <span className="badge bg-info ms-2">RMS Request</span>
              )}
              {selectedTicket && isNewVisitor(selectedTicket.activity_description) && (
                <span className="badge bg-warning ms-2">
                  <FontAwesomeIcon icon={faFlag} className="me-1" />
                  New Visitor
                </span>
              )}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => setShowTicketModal(false)}
            ></button>
          </div>
          <div className="modal-body p-4">
            {selectedTicket && (() => {
              const serviceType = getServiceType(selectedTicket)

              if (serviceType.label !== "AMC/WRT Covered") {
                return (
                  <div className="alert alert-warning mb-3">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    <strong>Service Notice:</strong> {serviceType.label}. This ticket may require chargeable service.
                  </div>
                )
              }

              return null
            })()}

            {selectedTicket && (
              <div className="row g-4">
                {/* Customer Information Card */}
                <div className="col-lg-6">
                  <div className="card h-100 border-0 shadow-sm">
                    <div className="card-header bg-light border-0">
                      <h6 className="card-title text-primary mb-0 d-flex align-items-center">
                        <FontAwesomeIcon icon={faUser} className="me-2" />
                        Customer Information
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                            <strong className="text-muted">Name:</strong>
                            <span className="fw-semibold">{selectedTicket.complaintname}</span>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                            <strong className="text-muted">Mobile:</strong>
                            <span className="fw-semibold">
                              <FontAwesomeIcon icon={faPhone} className="me-1 text-success" />
                              {selectedTicket.complaintmobile || "—"}
                            </span>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                            <strong className="text-muted">Email:</strong>
                            <span className="fw-semibold">
                              <FontAwesomeIcon icon={faEnvelope} className="me-1 text-success" />
                              {selectedTicket.complaintemail || "—"}
                            </span>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>

                {/* Assignment Information Card */}
                <div className="col-lg-6">
                  <div className="card h-100 border-0 shadow-sm">
                    <div className="card-header bg-light border-0">
                      <h6 className="card-title text-primary mb-0 d-flex align-items-center">
                        <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                        Assignment Information
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                            <strong className="text-muted">Assigned To:</strong>
                            <span className="fw-semibold">
                              <FontAwesomeIcon icon={faUserCheck} className="me-1 text-success" />
                              {selectedTicket.assignedToName}
                            </span>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                            <strong className="text-muted">Ticket ID:</strong>
                            <span className="fw-semibold text-primary">CBR-{selectedTicket.ticket_id}</span>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                            <strong className="text-muted">Status:</strong>
                            <span className={getBadgeClass(selectedTicket.status)}>
                              {getStatusDescription(selectedTicket.status)}
                            </span>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                            <strong className="text-muted">Created:</strong>
                            <span className="fw-semibold">
                              <FontAwesomeIcon icon={faClock} className="me-1 text-info" />
                              {selectedTicket.created_at
                                ? new Date(selectedTicket.created_at).toLocaleString("en-GB")
                                : "—"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {isNewVisitor(selectedTicket.activity_description) && (
                        <div className="alert alert-warning mt-3 mb-0">
                          <FontAwesomeIcon icon={faFlag} className="me-2" />
                          <strong>New Visitor Alert:</strong> This appears to be a new/unregistered user requiring
                          special attention.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Complaint/Service Details Card */}
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-light border-0">
                      <h6 className="card-title text-primary mb-0 d-flex align-items-center">
                        <FontAwesomeIcon icon={faClipboardQuestion} className="me-2" />
                        {selectedTicket.complaintProcessed?.type === "RMS" ? "Service Details" : "Ticket Details"}
                      </h6>
                    </div>
                    <div className="card-body">
                      {selectedTicket.complaintProcessed?.type === "RMS" ? (
                        <div className="p-4 bg-info bg-opacity-10 rounded border-start border-info border-4">
                          <div className="d-flex align-items-center mb-2">
                            <FontAwesomeIcon icon={faServer} className="text-info me-2" />
                            <strong className="text-info">RMS Schedule Request</strong>
                          </div>
                          <div className="text-muted">{selectedTicket.activity_description}</div>
                        </div>
                      ) : Array.isArray(selectedTicket.complaintProcessed?.displayText) &&
                        selectedTicket.complaintProcessed.displayText.length > 0 ? (
                        <div className="space-y-3">
                          {selectedTicket.complaintProcessed.displayText.map((complaint, idx) => (
                            <div
                              key={idx}
                              className="p-3 bg-primary bg-opacity-10 rounded border-start border-primary border-4 mb-3"
                            >
                              <div className="d-flex align-items-start">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-primary me-2 mt-1" />
                                <div className="flex-grow-1">
                                  <strong className="text-primary">Ticket Issue #{idx + 1}</strong>
                                  <div className="mt-1">{complaint}</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <FontAwesomeIcon icon={faInfoCircle} className="text-muted mb-2" size="2x" />
                          <div className="text-muted">No complaint details available</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Ticket Activity History Card */}
                {ticketHistory.length > 0 && (
                  <div className="col-12">
                    <div className="card border-0 shadow-sm">
                      <div className="card-header bg-light border-0">
                        <h6 className="card-title text-primary mb-0 d-flex align-items-center">
                          <FontAwesomeIcon icon={faHistory} className="me-2" />
                          Ticket Journey (Activities)
                        </h6>
                      </div>
                      <div className="card-body">
                        {historyLoading ? (
                          <div className="text-center text-muted py-4">
                            <FontAwesomeIcon icon={faSpinner} className="fa-spin me-2" />
                            Loading activity history...
                          </div>
                        ) : (
                          <div className="timeline">
                            {ticketHistory.map((activity, idx) => (
                              <div key={activity.id || idx} className="timeline-item mb-3">
                                <div className="d-flex">
                                  <div className="timeline-marker me-3">
                                    <div
                                      className="bg-primary rounded-circle d-flex align-items-center justify-content-center"
                                      style={{ width: "30px", height: "30px" }}
                                    >
                                      <FontAwesomeIcon icon={faHistory} className="text-white" size="sm" />
                                    </div>
                                  </div>
                                  <div className="timeline-content flex-grow-1">
                                    <div className="card border-0 shadow-sm">
                                      <div className="card-body p-3">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                          <div>
                                            <strong className="text-primary">
                                              {activity.status
                                                ? `Status: ${getStatusDescription(activity.status)}`
                                                : "Activity Update"}
                                            </strong>
                                            {activity.assignedTo && (
                                              <div className="text-muted small">
                                                Assigned to:{" "}
                                             {activity.assignedToName || "Unknown"}
                                              </div>
                                            )}
                                          </div>
                                          <small className="text-muted">
                                            <FontAwesomeIcon icon={faClock} className="me-1" />
                                            {activity.createdAt || activity.created_at
                                              ? new Date(activity.createdAt || activity.created_at).toLocaleString(
                                                "en-GB",
                                              )
                                              : "—"}
                                          </small>
                                        </div>
                                        {activity.description && (
                                          <div className="text-muted">{activity.description}</div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <>
      <style>
        {`
          .ticket-dashboard {
            background: #f8fafc;
            border-radius: 12px;
            padding: 4px;
          }
          .summary-card {
            transition: all 0.2s ease;
          }
          .summary-card:hover {
            transform: translateY(-1px);
          }
          .summary-icon-wrap {
            width: 42px;
            height: 42px;
          }
          .filter-card .card-header,
          .table-shell .card-header {
            background: #ffffff;
            border-bottom: 1px solid #eef1f5;
          }
          .filter-collapse {
            max-height: 0;
            opacity: 0;
            overflow: hidden;
            transition: max-height 0.3s ease, opacity 0.2s ease;
          }
          .filter-collapse.expanded {
            max-height: 1600px;
            opacity: 1;
          }
          .device-chip-scroll::-webkit-scrollbar {
            height: 0;
            width: 0;
          }
          .device-chip-scroll::-webkit-scrollbar-thumb {
            background: #ced4da;
            border-radius: 999px;
          }
          .device-chip-scroll {
            scrollbar-width: none;
            -ms-overflow-style: none;
            scroll-behavior: smooth;
          }
          .device-chip-btn {
            transition: all 0.2s ease;
            white-space: nowrap;
            border-radius: 999px;
            font-weight: 500;
            padding: 0.3rem 0.85rem;
          }
          .device-chip-btn:hover {
            transform: translateY(-1px) scale(1.01);
            box-shadow: 0 0.125rem 0.35rem rgba(15, 23, 42, 0.15);
          }
          .device-chip-wrap {
            display: flex;
            gap: 0.5rem;
            overflow-x: auto;
            flex-wrap: nowrap;
            padding-bottom: 0.25rem;
          }
          .device-chip-collapsible {
            max-height: 120px;
            overflow: hidden;
            transition: max-height 0.3s ease;
          }
          .device-chip-collapsible.expanded {
            max-height: 720px;
          }
          @media (min-width: 768px) {
            .device-chip-wrap {
              overflow-x: visible;
              flex-wrap: wrap;
            }
          }
          .ticket-table thead th {
            position: sticky;
            top: 0;
            z-index: 2;
            background: #1f2937 !important;
          }
          .ticket-table td,
          .ticket-table th {
            padding-top: 0.8rem !important;
            padding-bottom: 0.8rem !important;
          }
          .ticket-row {
            transition: background-color 0.15s ease;
          }
          .ticket-row:hover {
            background-color: #f6f9ff;
          }
          .ticket-table tbody tr td {
            border-bottom: 1px solid #eef2f7;
          }
          .action-btn {
            min-width: 84px;
            font-weight: 500;
            transition: all 0.2s ease;
          }
          .action-btn:hover {
            transform: translateY(-1px);
          }
        `}
      </style>
      <ToastContainer />
      <div className="container-fluid py-3 px-2 px-md-4">
        <div className="ticket-dashboard">
        {/* Header with Summary Metrics */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm bg-primary text-white">
              <div className="card-body py-3">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
                  <div>
                    <h3 className="mb-1 fw-bold d-flex align-items-center">
                      <FontAwesomeIcon icon={faTicketAlt} className="me-2" />
                      ATai Ticket Bot System
                    </h3>
                    <div className="d-flex flex-wrap gap-3 mt-1">
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faUsers} className="me-1" />
                        <span className="small">Service Engineer Count: {staffList.length}</span>
                      </div>
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faUserClock} className="me-1" />
                        <span className="small">Unassigned Tickets Count: {unassignedCount}</span>
                      </div>
                      {/* <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faFlag} className="me-1" />
                        <span className="small">New Customer Count: {newVisitorCount}</span>
                      </div> */}
                    </div>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      className={`btn btn-sm ${autoRefresh ? "btn-warning" : "btn-light"}`}
                      onClick={() => setAutoRefresh(!autoRefresh)}
                      title={autoRefresh ? "Auto-refresh ON" : "Auto-refresh OFF"}
                    >
                      <FontAwesomeIcon icon={faBell} className="me-1" />
                      Auto
                    </button>
                    <button className="btn btn-light btn-sm" onClick={exportToCSV} title="Export to CSV">
                      <FontAwesomeIcon icon={faDownload} />
                    </button>
                    <button className="btn btn-success btn-sm" onClick={refreshData} disabled={refreshing}>
                      <FontAwesomeIcon icon={faSync} className={`${refreshing ? "fa-spin" : ""}`} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Status Overview Cards - Summary Header Block */}
        <div className="row g-3 mb-4">
          {Object.entries(statusConfig).map(([status, config]) => {
            if (status === "No-Response" && ticketCounts.noResponseCount === 0) return null

            const IconComponent = config.icon
            const isActive = filterStatus === status
            const count = statusCounts[status]

            return (
              <div key={status} className="col-6 col-lg">
                <div
                  className={`card h-100 border-0 shadow-sm summary-card ${isActive ? "border border-primary border-2" : ""}`}
                  onClick={() => {
                    setFilterStatus(status)
                    setCurrentPage(1)
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <div className="card-body p-3 d-flex align-items-center gap-3">
                    <div
                      className={`summary-icon-wrap rounded-circle d-inline-flex align-items-center justify-content-center ${config.bgClass} bg-opacity-10 flex-shrink-0`}
                    >
                      <IconComponent className={`text-${config.color}`} size={17} />
                    </div>
                    <div>
                      <small className="text-muted d-block">{status === "All" ? "Total Tickets" : status}</small>
                      <h4 className={`mb-0 fw-bold text-${config.color}`}>{count}</h4>
                      <small className="text-muted">{config.description}</small>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Filter Bar */}
        <div className="card border-0 bg-white shadow-sm rounded-3 mb-4 filter-card">
          <div
            className="card-header py-2 px-3 d-flex align-items-center justify-content-between cursor-pointer"
            role="button"
            onClick={() => setShowFilters((prev) => !prev)}
            aria-expanded={showFilters}
            style={{ cursor: "pointer" }}
          >
            <h6 className="mb-0 fw-semibold d-flex align-items-center text-dark">
              <FontAwesomeIcon icon={faFilter} className="me-2 text-primary" />
              Filters
            </h6>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={(e) => {
                e.stopPropagation()
                setShowFilters((prev) => !prev)
              }}
            >
              <FontAwesomeIcon icon={showFilters ? faChevronUp : faChevronDown} className="me-1" />
              {showFilters ? "Hide" : "Show"}
            </button>
          </div>
          <div className={`filter-collapse ${showFilters ? "expanded" : ""}`}>
            <div className="card-body p-3 bg-light bg-opacity-25">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
              <small className="text-muted">Apply filters to narrow down tickets quickly</small>
              <div className="d-flex gap-2 flex-wrap">
                {quickFilters.map((filter) => (
                  <button
                    key={filter.key}
                    className={`btn btn-sm ${quickFilter === filter.key ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setQuickFilter(quickFilter === filter.key ? "" : filter.key)}
                  >
                    <FontAwesomeIcon icon={filter.icon} className="me-1" />
                    {filter.label}
                    {filter.key === "not_in_amc" && (
                      <span className={`badge ms-2 ${quickFilter === filter.key ? "bg-light text-primary" : "bg-primary text-white"}`}>
                        {chargeableCount}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="row g-2 mb-2">
              <div className="col-12 col-lg-4">
                <div className="input-group input-group-sm">
                  <span className="input-group-text">
                    <FontAwesomeIcon icon={faSearch} />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by ticket ID, customer name, mobile, or complaint..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-12 col-sm-6 col-lg-2">
                <select
                  className="form-select form-select-sm"
                  value={rmsFilter}
                  onChange={(e) => setRmsFilter(e.target.value)}
                >
                  <option value="All">All Ticket Types</option>
                  <option value="RMS">RMS Tickets Only</option>
                  <option value="Non-RMS">Non-RMS Tickets Only</option>
                </select>
              </div>
              <div className="col-12 col-sm-6 col-lg-2">
                <select
                  className="form-select form-select-sm"
                  value={agentFilter}
                  onChange={(e) => setAgentFilter(e.target.value)}
                >
                  <option value="All">All Agents</option>
                  <option value="0">Unassigned</option>
                  {staffList.map((staff) => (
                    <option key={staff.user_id} value={staff.user_id}>
                      {staff.user_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-6 col-lg-2">
                <select
                  className="form-select form-select-sm"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <option value="All">All Time</option>
                  <option value="Today">Today</option>
                  <option value="ThisMonth">This Month</option>
                </select>
              </div>
              <div className="col-6 col-lg-2 d-grid">
                <button className="btn btn-outline-secondary btn-sm" onClick={handleReset}>
                  Clear All
                </button>
              </div>
            </div>

            {showSearchFilters && (
              <div className="row g-2 mb-3">
                <div className="col-6 col-md-3">
                  <input
                    className="form-control form-control-sm"
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    placeholder="From Date"
                  />
                </div>
                <div className="col-6 col-md-3">
                  <input
                    className="form-control form-control-sm"
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    placeholder="To Date"
                  />
                </div>
                <div className="col-12 col-md-2 d-grid">
                  <button className="btn btn-primary btn-sm" onClick={handleFilter}>
                    Apply
                  </button>
                </div>
                <div className="col-12 col-md-4 d-flex gap-2 flex-wrap align-items-center">
                  {rmsFilter !== "All" && (
                    <span className="badge bg-info d-flex align-items-center">
                      {rmsFilter} Tickets
                      <button
                        className="btn-close btn-close-white ms-2"
                        style={{ fontSize: "0.6em" }}
                        onClick={() => setRmsFilter("All")}
                      ></button>
                    </span>
                  )}
                  {agentFilter !== "All" && (
                    <span className="badge bg-success d-flex align-items-center">
                      Agent:{" "}
                      {agentFilter === "0"
                        ? "Unassigned"
                        : staffList.find((s) => s.user_id.toString() === agentFilter.toString())?.user_name ||
                        "Unknown"}
                      <button
                        className="btn-close btn-close-white ms-2"
                        style={{ fontSize: "0.6em" }}
                        onClick={() => setAgentFilter("All")}
                      ></button>
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="border-top pt-3">
              <div className="d-flex align-items-center mb-2">
                <FontAwesomeIcon icon={faServer} className="me-2 text-primary" />
                <span className="small fw-semibold text-muted">Device Selection</span>
              </div>

              <div className={`device-chip-collapsible ${showAllDevices ? "expanded" : ""}`}>
                <div className="device-chip-wrap device-chip-scroll">
                  <button
                    className={`btn btn-sm rounded-pill device-chip-btn ${deviceFilter === "All" ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => {
                      setDeviceFilter("All")
                      setCurrentPage(1)
                    }}
                  >
                    All Devices
                  </button>

                  {visibleDeviceOptions.map((deviceId) => (
                    <button
                      key={deviceId}
                      className={`btn btn-sm rounded-pill device-chip-btn ${deviceFilter === deviceId ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => {
                        setDeviceFilter(deviceId)
                        setCurrentPage(1)
                      }}
                    >
                      <FontAwesomeIcon icon={faServer} className="me-1" />
                      Sticker ID: {deviceId} ({deviceAggregation[deviceId]?.total || 0})
                    </button>
                  ))}
                </div>
              </div>

              {deviceFilterOptions.length > DEVICE_COLLAPSE_LIMIT && (
                <div className="mt-2">
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-decoration-none px-0"
                    onClick={() => setShowAllDevices((prev) => !prev)}
                  >
                    {showAllDevices ? "Show Less" : `Show More (${hiddenDeviceCount} devices)`}
                  </button>
                </div>
              )}

              {deviceFilter !== "All" && deviceAggregation[deviceFilter] && (
                <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
                  {deviceAggregation[deviceFilter].OPN > 0 && (
                    <span className="badge bg-light text-dark border">Open: {deviceAggregation[deviceFilter].OPN}</span>
                  )}
                  {deviceAggregation[deviceFilter].INP > 0 && (
                    <span className="badge bg-light text-dark border">In Progress: {deviceAggregation[deviceFilter].INP}</span>
                  )}
                  {deviceAggregation[deviceFilter].CRS > 0 && (
                    <span className="badge bg-light text-dark border">Closed: {deviceAggregation[deviceFilter].CRS}</span>
                  )}
                </div>
              )}
            </div>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <small className="text-muted">
                <span className="d-block fw-semibold text-dark mb-1">{deviceSummaryText}</span>
                Showing <span className="fw-bold">{paginatedTickets.length}</span> of{" "}
                <span className="fw-bold">{filteredTickets.length}</span> tickets
                {(rmsFilter !== "All" || agentFilter !== "All") && (
                  <span className="text-primary ms-2">
                    ({rmsFilter !== "All" ? `${rmsFilter} ` : ""}
                    {agentFilter !== "All"
                      ? `${agentFilter === "0" ? "Unassigned" : staffList.find((s) => s.user_id.toString() === agentFilter.toString())?.user_name || "Unknown"} Agent`
                      : ""}
                    )
                  </span>
                )}
              </small>
              {filteredTickets.length !== tickets.length && (
                <small className="text-primary">
                  <FontAwesomeIcon icon={faFilter} className="me-1" />
                  Filters applied
                </small>
              )}
            </div>
          </div>
        </div>

        {/* Professional Ticket Table - No vertical scroll for ≤10 entries */}
        <div className="card border-0 bg-white shadow-sm table-shell mb-3">
          <div className="card-header py-2 px-3 d-flex align-items-center justify-content-between">
            <h6 className="mb-0 fw-semibold d-flex align-items-center">
              <FontAwesomeIcon icon={faTicketAlt} className="me-2 text-primary" />
              Ticket List
            </h6>
            <small className="text-muted">{filteredTickets.length} Records</small>
          </div>
          <div className="card-body p-0">
            <div
              className="table-responsive"
              style={{
                maxHeight: paginatedTickets.length > 10 ? "160vh" : "auto",
                overflowY: paginatedTickets.length > 10 ? "auto" : "visible",
              }}
            >
              <table className="table table-hover align-middle mb-0 ticket-table" style={{ maxHeight: "none", overflowY: "visible" }}>
                <thead className="table-dark">
                  <tr>
                    <th className="border-0 py-2 px-3" style={{ width: "100px" }}>
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faHashtag} className="me-1" />
                        <span className="fw-bold">ID</span>
                      </div>
                    </th>
                    <th className="border-0 py-2 px-3" style={{ width: "200px" }}>
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faUser} className="me-1" />
                        <span className="fw-bold">Customer</span>
                      </div>
                    </th>
                    {/* <th className="border-0 py-2 px-3" style={{ width: "120px" }}>
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faPhone} className="me-1" />
                        <span className="fw-bold">Mobile</span>
                      </div>
                    </th> */}
                    <th className="border-0 py-2 px-3" style={{ width: "300px" }}>
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faClipboardQuestion} className="me-1" />
                        <span className="fw-bold">Ticket Details</span>
                      </div>
                    </th>
                    <th className="border-0 py-2 px-3" style={{ width: "180px" }}>
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faUserCheck} className="me-1" />
                        <span className="fw-bold">Assigned To</span>
                      </div>
                    </th>
                    <th className="border-0 py-2 px-3" style={{ width: "100px" }}>
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                        <span className="fw-bold">Status</span>
                      </div>
                    </th>
                    <th className="border-0 py-2 px-3" style={{ width: "80px" }}>
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faEye} className="me-1" />
                        <span className="fw-bold">Action</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(8)].map((_, i) => (
                      <tr key={i}>
                        <td colSpan="7" className="border-0 py-2">
                          <Skeleton height={35} />
                        </td>
                      </tr>
                    ))
                  ) : paginatedTickets.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-5 border-0">
                        <FaInbox size={48} className="text-muted mb-3 opacity-50" />
                        <h5 className="text-muted">No tickets found</h5>
                        <p className="text-muted">Try adjusting your filters or search criteria</p>
                      </td>
                    </tr>
                  ) : (
                    paginatedTickets.map((ticket) => (
                      <tr
                        key={ticket.ticket_id}
                        className={`ticket-row ${isNewVisitor(ticket.activity_description) ? "table-warning" : ""}`}
                      >
                        <td className="border-0 py-2 px-3">
                          <button
                            className="btn btn-link p-0 fw-bold text-primary text-decoration-none"
                            onClick={() => {
                              setSelectedTicket(ticket)
                              setShowTicketModal(true)
                            }}
                          >
                            CBR-{ticket.ticket_id}
                          </button>
                        </td>
                        <td className="border-0 py-2 px-3">
                          <div className="d-flex flex-column">
                            <span className="fw-semibold">{ticket.complaintname}</span>
                            {/* Service Type Badge */}
                            {(() => {
                              const serviceType = getServiceType(ticket)

                              return (
                                <span className={`${serviceType.class} mt-1`} style={{ fontSize: "11px", width: "fit-content" }}>
                                  <FontAwesomeIcon icon={serviceType.icon} className="me-1" />
                                  {serviceType.label}
                                </span>
                              )
                            })()}
                            {ticket.created_by_name && (
                              <small className="text-muted">Created by: {ticket.created_by_name}</small>
                            )}
                            {isNewVisitor(ticket.activity_description) && (
                              <small className="text-warning">
                                <FontAwesomeIcon icon={faFlag} className="me-1" />
                                New Visitor
                              </small>
                            )}
                          </div>
                        </td>
                        {/* <td className="border-0 py-2 px-3">
                          <span className="fw-semibold">{ticket.complaintmobile || "—"}</span>
                        </td> */}

                        <td className="border-0 py-2 px-3">
                          <div className="complaint-cell">
                            {ticket.complaintProcessed?.type === "RMS" ? (
                              <div>
                                <div className="fw-semibold text-info d-flex align-items-center">
                                  <FontAwesomeIcon icon={faServer} className="me-1" />
                                  RMS Schedule Request
                                </div>

                                {/* Show all device IDs for RMS */}
                                {Array.isArray(ticket.complaints) && ticket.complaints.length > 0 && (
                                  <div className="mt-1">
                                    <small className="text-primary fw-semibold">
                                      Sticker IDs:{" "}
                                      {[
                                        ...new Set(ticket.complaints.map((c) => c.device_id)) // unique IDs
                                      ]
                                        .map((id) => (id === 0 || id === "0" ? "New Customer Enquiry" : id))
                                        .join(", ")}
                                    </small>

                                  </div>
                                )}
                              </div>
                            ) : (
                              <div>
                                {Array.isArray(ticket.complaintProcessed?.displayText) &&
                                  ticket.complaintProcessed.displayText.length > 0 ? (
                                  <div>
                                    {ticket.complaintProcessed.displayText.slice(0, 1).map((complaint, idx) => (
                                      <div key={idx} className="complaint-item mb-1">
                                        <small className="text-muted d-block">{complaint}</small>
                                      </div>
                                    ))}
                                    {ticket.complaintProcessed.displayText.length > 1 && (
                                      <small className="text-info">
                                        +{ticket.complaintProcessed.displayText.length - 1} more complaints
                                      </small>
                                    )}

                                    {/* Show device IDs if available */}
                                    {Array.isArray(ticket.complaints) && ticket.complaints.length > 0 && (
                                      <div className="mt-1">
                                        <small className="text-primary fw-semibold">
                                          {[
                                            ...new Set(ticket.complaints.map((c) => c.device_id)),
                                          ].every((id) => id === 0 || id === "0")
                                            ? "New Customer Enquiry"
                                            : `Sticker ID: ${[...new Set(ticket.complaints.map((c) => c.device_id))]
                                              .map((id) => (id === 0 || id === "0" ? "New Customer Enquiry" : id))
                                              .join(", ")
                                            }`}
                                        </small>
                                      </div>
                                    )}

                                  </div>
                                ) : (
                                  <span className="text-muted">No complaints</span>
                                )}
                              </div>
                            )}
                          </div>
                        </td>

                        <td className="border-0 py-2 px-3">
                          {canAssignTicket(ticket.status) ? (
                            <div className="dropdown">
                              <button
                                className={`btn btn-sm dropdown-toggle action-btn ${ticket.assignedName === "Unassigned" ? "btn-outline-warning" : "btn-outline-primary"}`}
                                data-bs-toggle="dropdown"
                                disabled={assigningTickets.has(ticket.ticket_id)}
                                style={{ minWidth: "70px" }}
                              >
                                {assigningTickets.has(ticket.ticket_id) ? (
                                  <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                                ) : (
                                  ticket.assignedName
                                )}
                              </button>
                              <ul className="dropdown-menu">
                                {staffList.map((staff) => (
                                  <li key={staff.user_id}>
                                    <button
                                      className={`dropdown-item ${Number.parseInt(ticket.assigned_to) === Number.parseInt(staff.user_id)
                                        ? "active"
                                        : ""
                                        }`}
                                      onClick={() => handleAssignAgent(ticket.ticket_id, staff.user_id)}
                                    >
                                      {staff.user_name}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : (
                            <div className="d-flex align-items-center text-muted">
                              <FontAwesomeIcon icon={faLock} className="me-1" />
                              <span className="small">{ticket.assignedName}</span>
                            </div>
                          )}
                        </td>
                        {/* <td className="border-0 py-2 px-3">
                          <span className={`${getBadgeClass(ticket.status)} px-2 py-1 small`}>
                            {getStatusDescription(ticket.status)}
                          </span>
                        </td> */}
                        <td className="border-0 py-2 px-3">
                          {canUpdateFinalBillingStatus(ticket.status) ? (
                            <div className="dropdown">
                              <button
                                className="btn btn-sm btn-outline-dark dropdown-toggle action-btn"
                                data-bs-toggle="dropdown"
                              >
                                {getStatusDescription(ticket.status)}
                              </button>

                              <ul className="dropdown-menu">
                                {ticket.status !== "INV" && (
                                  <li>
                                    <button
                                      className="dropdown-item"
                                      onClick={() => openBillingModal(ticket.ticket_id, "INV")}
                                    >
                                      Mark as Invoiced
                                    </button>
                                  </li>
                                )}

                                {ticket.status !== "NIV" && (
                                  <li>
                                    <button
                                      className="dropdown-item"
                                      onClick={() => openBillingModal(ticket.ticket_id, "NIV")}
                                    >
                                      Mark as Not Invoiced
                                    </button>
                                  </li>
                                )}
                              </ul>

                            </div>
                          ) : (
                            <span className={`${getBadgeClass(ticket.status)} px-2 py-1 small`}>
                              {getStatusDescription(ticket.status)}
                            </span>
                          )}
                        </td>

                        <td className="border-0 py-2 px-3">
                          <button
                            className="btn btn-outline-info btn-sm d-flex align-items-center action-btn"
                            onClick={() => {
                              setSelectedTicket(ticket)
                              setShowTicketModal(true)
                            }}
                            title="View Ticket Details"
                            style={{ minWidth: "70px" }}
                          >
                            <FontAwesomeIcon icon={faEye} className="me-1" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-3">
            <nav>
              <ul className="pagination pagination-sm">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                </li>
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? "active" : ""}`}>
                        <button className="page-link" onClick={() => setCurrentPage(pageNumber)}>
                          {pageNumber}
                        </button>
                      </li>
                    )
                  }
                  return null
                })}
                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}

        <TicketDetailModal />
        {showBillingModal && (
          <>
            <div className="modal fade show" style={{ display: "block" }}>
              <div className="modal-dialog">
                <div className="modal-content border-0 shadow">
                  <div className="modal-header bg-dark text-white">
                    <h5 className="modal-title">
                      {billingStatus === "INV"
                        ? "Mark Ticket as Invoiced"
                        : "Mark Ticket as Not Invoiced"}
                    </h5>
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      onClick={() => setShowBillingModal(false)}
                    ></button>
                  </div>

                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Billing Comment <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        rows="4"
                        value={billingComment}
                        onChange={(e) => setBillingComment(e.target.value)}
                        placeholder="Enter billing remarks (Invoice No, Amount, Notes, etc.)"
                      ></textarea>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setShowBillingModal(false)}
                      disabled={billingLoading}
                    >
                      Cancel
                    </button>

                    <button
                      className="btn btn-dark"
                      onClick={handleBillingSubmit}
                      disabled={billingLoading}
                    >
                      {billingLoading ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} className="fa-spin me-2" />
                          Processing...
                        </>
                      ) : (
                        "Confirm"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="modal-backdrop fade show"
              onClick={() => setShowBillingModal(false)}
            ></div>
          </>
        )}

        </div>
      </div>

      {showTicketModal && <div className="modal-backdrop fade show" onClick={() => setShowTicketModal(false)}></div>}
    </>
  )
}

export default TicketAdmin
